
document.documentElement.classList.remove('no-js');

class SvaicoPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.form = null;
    this.results = null;
    this.abortController = null;
    this.cache = new Map();
    this.activeIndex = -1;
    this.requestId = 0;
    this.loadingTimer = null;
    this.boundDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.liveSearchReady === 'true') return;

    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form[role="search"], form');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;

    this.dataset.liveSearchReady = 'true';
    this.results.id = this.results.id || `SvaicoPredictiveResults-${Math.random().toString(36).slice(2, 9)}`;
    this.results.setAttribute('role', 'listbox');
    this.results.setAttribute('aria-label', 'Product suggestions');

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.results.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('spellcheck', 'false');

    this.input.addEventListener('input', this.debounce(() => this.onChange(), 140));
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.results.addEventListener('mousemove', (event) => this.onMouseMove(event));
    this.results.addEventListener('click', (event) => this.onResultsClick(event));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    document.addEventListener('click', this.boundDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    this.abortController?.abort();
    clearTimeout(this.loadingTimer);
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  onFocus() {
    const term = this.input.value.trim();
    if (term.length >= 2 && this.results.innerHTML.trim()) this.open();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  async onChange() {
    const term = this.input.value.trim();
    this.activeIndex = -1;

    if (term.length < 2) {
      this.abortController?.abort();
      this.results.innerHTML = '';
      this.close();
      return;
    }

    const cacheKey = term.toLocaleLowerCase();
    if (this.cache.has(cacheKey)) {
      this.render(this.cache.get(cacheKey), term);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const currentRequestId = ++this.requestId;

    clearTimeout(this.loadingTimer);
    this.loadingTimer = setTimeout(() => {
      if (currentRequestId !== this.requestId) return;
      this.results.innerHTML = '<div class="predictive-state" role="status"><span class="predictive-spinner" aria-hidden="true"></span>Searching products…</div>';
      this.open();
    }, 120);

    try {
      const root = window.Shopify?.routes?.root || '/';
      const strictReferenceSearch = this.isReferenceLike(term);
      const predictiveQuery = strictReferenceSearch ? this.quoteSearchPhrase(term) : term;
      const params = new URLSearchParams({ q: predictiveQuery });
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '5');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'show');
      params.set(
        'resources[options][fields]',
        strictReferenceSearch
          ? 'body,variants.sku,variants.barcode'
          : 'title,body,product_type,tag,vendor,variants.sku,variants.barcode,variants.title'
      );

      const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`Predictive search ${response.status}: ${detail}`);
      }

      const payload = await response.json();
      if (currentRequestId !== this.requestId) return;

      const products = payload?.resources?.results?.products || [];
      this.cache.set(cacheKey, products);
      if (this.cache.size > 40) this.cache.delete(this.cache.keys().next().value);
      this.render(products, term);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[SVAICO live search]', error);
      if (currentRequestId !== this.requestId) return;
      this.renderError(term);
    } finally {
      clearTimeout(this.loadingTimer);
    }
  }

  render(products, term) {
    if (this.input.value.trim() !== term) return;

    const rows = products.map((product, index) => this.productRow(product, index)).join('');
    const searchUrl = this.searchUrl(term);

    if (!rows) {
      this.results.innerHTML = `
        <div class="predictive-state" role="status">No matching products found.</div>
        <a class="predictive-view-all" href="${this.escapeAttribute(searchUrl)}" role="option" data-predictive-option>
          Search for “${this.escapeHtml(term)}”
          <span aria-hidden="true">→</span>
        </a>`;
    } else {
      this.results.innerHTML = `
        <div class="predictive-heading">Product suggestions</div>
        <div class="predictive-list">${rows}</div>
        <a class="predictive-view-all" href="${this.escapeAttribute(searchUrl)}" role="option" data-predictive-option>
          View all results for “${this.escapeHtml(term)}”
          <span aria-hidden="true">→</span>
        </a>`;
    }

    this.activeIndex = -1;
    this.open();
  }

  renderError(term) {
    this.results.innerHTML = `
      <div class="predictive-state predictive-state--error" role="status">Live suggestions are temporarily unavailable.</div>
      <a class="predictive-view-all" href="${this.escapeAttribute(this.searchUrl(term))}" role="option" data-predictive-option>
        Search for “${this.escapeHtml(term)}”
        <span aria-hidden="true">→</span>
      </a>`;
    this.open();
  }


  placeholderImage(product) {
    const assets = window.SVAICO_PLACEHOLDER_ASSETS || {};
    const text = [product?.title, product?.type, product?.product_type].filter(Boolean).join(' ').toLowerCase();
    let key = 'generic';
    if (/timing\s*(belt|kit|chain)|timing system/.test(text)) key = 'timing';
    else if (/fuel injector|\binjector\b/.test(text)) key = 'injector';
    else if (/brake disc|brake rotor/.test(text)) key = 'brake';
    else if (/shock absorber/.test(text)) key = 'shock';
    else if (/oil filter/.test(text)) key = 'oilFilter';
    else if (/spark plug|ignition system/.test(text)) key = 'sparkPlug';
    else if (/\bradiator\b/.test(text)) key = 'radiator';
    else if (/crankshaft|main bearing|connecting rod bearing|conrod bearing/.test(text)) key = 'engine';
    return assets[key] || assets.generic || '';
  }

  productRow(product, index) {
    const imageUrl = product.featured_image?.url || product.image || '';
    const imageAlt = product.featured_image?.alt || product.title || '';
    const meta = [product.vendor, product.type].filter(Boolean).join(' · ');
    const price = this.formatMoney(product.price);
    const availability = product.available ? 'Available to order' : 'View product';

    return `
      <a class="predictive-item" href="${this.escapeAttribute(product.url || '#')}" role="option"
         id="${this.results.id}-option-${index}" data-predictive-option>
        <span class="predictive-image">
          ${imageUrl
            ? `<img src="${this.escapeAttribute(this.resizeImage(imageUrl, 120))}" alt="${this.escapeAttribute(imageAlt)}" loading="lazy" width="58" height="58">`
            : `<img src="${this.escapeAttribute(this.placeholderImage(product))}" alt="${this.escapeAttribute(imageAlt)} — reference illustration" loading="lazy" width="58" height="58" class="svaico-placeholder-image">`}
        </span>
        <span class="predictive-copy">
          ${meta ? `<span class="predictive-vendor">${this.escapeHtml(meta)}</span>` : ''}
          <span class="predictive-title">${this.escapeHtml(product.title || 'Product')}</span>
          <span class="predictive-availability">${availability}</span>
        </span>
        ${price ? `<span class="predictive-price">${this.escapeHtml(price)}</span>` : ''}
      </a>`;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    const options = this.options();
    if (!options.length || this.input.getAttribute('aria-expanded') !== 'true') return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive(Math.min(this.activeIndex + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      options[this.activeIndex]?.click();
    }
  }

  onMouseMove(event) {
    const option = event.target.closest('[data-predictive-option]');
    if (!option) return;
    const index = this.options().indexOf(option);
    if (index >= 0) this.setActive(index, false);
  }

  onResultsClick(event) {
    const option = event.target.closest('[data-predictive-option]');
    if (option) this.close();
  }

  onSubmit(event) {
    const term = this.input?.value.trim() || '';
    if (!term) return;

    if (this.isReferenceLike(term)) {
      const strictQuery = this.strictSearchQuery(term);
      let hiddenQuery = this.form.querySelector('input[data-svaico-strict-query]');
      if (!hiddenQuery) {
        hiddenQuery = document.createElement('input');
        hiddenQuery.type = 'hidden';
        hiddenQuery.name = 'q';
        hiddenQuery.dataset.svaicoStrictQuery = 'true';
        this.form.appendChild(hiddenQuery);
      }
      hiddenQuery.value = strictQuery;
      this.input.removeAttribute('name');

      let prefixInput = this.form.querySelector('input[name="options[prefix]"]');
      if (!prefixInput) {
        prefixInput = document.createElement('input');
        prefixInput.type = 'hidden';
        prefixInput.name = 'options[prefix]';
        this.form.appendChild(prefixInput);
      }
      prefixInput.value = 'none';
    }

    this.close();
  }

  isReferenceLike(term) {
    const value = String(term || '').trim();
    if (value.length < 3 || value.length > 64 || !/\d/.test(value)) return false;
    if (!/^[A-Za-z0-9À-ÿ][A-Za-z0-9À-ÿ\s._\-\/+]*$/.test(value)) return false;

    const tokens = value.split(/\s+/).filter(Boolean);
    const alphaOnlyTokens = tokens.filter((token) => /^[A-Za-zÀ-ÿ]+$/.test(token));
    if (alphaOnlyTokens.length >= 2) return false;

    return true;
  }

  quoteSearchPhrase(term) {
    const escaped = String(term || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
    return `"${escaped}"`;
  }

  strictSearchQuery(term) {
    // Shopify storefront search already searches product title/body plus full SKU and barcode.
    // A quoted phrase keeps part-number searches exact without exposing field-query syntax
    // (title:, variants.sku:, etc.) in the customer-facing URL/search box.
    return this.quoteSearchPhrase(term);
  }

  options() {
    return [...this.results.querySelectorAll('[data-predictive-option]')];
  }

  setActive(index, scroll = true) {
    const options = this.options();
    if (!options.length) return;

    options.forEach((option, optionIndex) => {
      option.classList.toggle('is-active', optionIndex === index);
      option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
    });

    this.activeIndex = index;
    const active = options[index];
    if (active?.id) this.input.setAttribute('aria-activedescendant', active.id);
    else this.input.removeAttribute('aria-activedescendant');
    if (scroll) active?.scrollIntoView({ block: 'nearest' });
  }

  open() {
    if (!this.results.innerHTML.trim()) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  close() {
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    this.classList.remove('is-open');
    this.activeIndex = -1;
  }

  searchUrl(term) {
    const root = window.Shopify?.routes?.root || '/';
    const strictReferenceSearch = this.isReferenceLike(term);
    const query = strictReferenceSearch ? this.strictSearchQuery(term) : term;
    const params = new URLSearchParams({ q: query, type: 'product' });
    params.set('options[prefix]', strictReferenceSearch ? 'none' : 'last');
    return `${root}search?${params.toString()}`;
  }

  formatMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'NOK';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  resizeImage(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}


class SvaicoSearchMatchEnhancer {
  constructor(grid) {
    this.grid = grid;
    this.term = String(grid?.dataset?.svaicoSearchTerm || '').trim();
    this.strict = grid?.dataset?.svaicoStrictSearch === 'true';
    this.termKey = this.normalizePartNumber(this.term);
  }

  init() {
    if (!this.grid || !this.termKey || !/\d/.test(this.term)) return;

    const cards = [...this.grid.querySelectorAll('.product-card')];
    cards.forEach((card, index) => {
      card.dataset.svaicoOriginalOrder = String(index);
      const match = this.findBestMatch(card);
      card.dataset.svaicoMatchRank = String(match?.rank || 0);
      if (match) this.renderReason(card, match);
    });

    // Keep Shopify's result set, but make exact structured part-number matches appear first.
    // This is especially useful when the same number exists as SKU, OE reference and cross-reference.
    cards
      .sort((a, b) => {
        const rankDiff = Number(b.dataset.svaicoMatchRank || 0) - Number(a.dataset.svaicoMatchRank || 0);
        if (rankDiff) return rankDiff;
        return Number(a.dataset.svaicoOriginalOrder || 0) - Number(b.dataset.svaicoOriginalOrder || 0);
      })
      .forEach((card) => this.grid.appendChild(card));
  }

  normalizePartNumber(value) {
    return String(value ?? '')
      .normalize('NFKC')
      .toLocaleUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  cleanText(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  parseMaybeJson(value) {
    let current = value;
    for (let i = 0; i < 2 && typeof current === 'string'; i += 1) {
      const trimmed = current.trim();
      if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('[') && !trimmed.startsWith('"'))) break;
      try { current = JSON.parse(trimmed); } catch (error) { break; }
    }
    return current;
  }

  splitNumbers(value) {
    const parsed = this.parseMaybeJson(value);
    if (Array.isArray(parsed)) {
      return [...new Set(parsed.flatMap((item) => this.splitNumbers(item)).filter(Boolean))];
    }
    if (parsed && typeof parsed === 'object') {
      return [...new Set(Object.values(parsed).flatMap((item) => this.splitNumbers(item)).filter(Boolean))];
    }
    if (parsed == null) return [];
    return [...new Set(String(parsed)
      .replace(/\u00a0/g, ' ')
      .split(/\s*(?:,|;|\n|\|)\s*/)
      .map((item) => this.cleanText(item))
      .filter(Boolean))];
  }

  referenceGroups(raw, fallbackBrand) {
    const parsed = this.parseMaybeJson(raw);
    const groups = [];
    const add = (brand, numbers) => {
      const cleanBrand = this.cleanText(brand) || fallbackBrand;
      const cleanNumbers = this.splitNumbers(numbers);
      if (!cleanNumbers.length) return;
      groups.push({ brand: cleanBrand, numbers: cleanNumbers });
    };

    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          add(
            item.brand ?? item.manufacturer ?? item.make ?? item.name ?? fallbackBrand,
            item.numbers ?? item.references ?? item.values ?? item.part_numbers ?? item.partNumbers ?? item.number ?? item.value
          );
        } else {
          add(fallbackBrand, item);
        }
      });
    } else if (parsed && typeof parsed === 'object') {
      Object.entries(parsed).forEach(([brand, numbers]) => add(brand, numbers));
    } else if (parsed != null && parsed !== '') {
      add(fallbackBrand, parsed);
    }

    return groups;
  }

  exactValueMatch(values) {
    const list = Array.isArray(values) ? values : [values];
    for (const value of list) {
      const clean = this.cleanText(value);
      if (clean && this.normalizePartNumber(clean) === this.termKey) return clean;
    }
    return '';
  }

  groupMatch(groups) {
    for (const group of groups) {
      for (const number of group.numbers || []) {
        if (this.normalizePartNumber(number) === this.termKey) {
          return { brand: group.brand || '', number: this.cleanText(number) };
        }
      }
    }
    return null;
  }

  findBestMatch(card) {
    const script = card.querySelector('[data-svaico-search-product-data]');
    if (!script) return null;

    let data;
    try { data = JSON.parse(script.textContent || '{}'); } catch (error) { return null; }

    const sku = this.exactValueMatch(data.skus || []);
    if (sku) return { rank: 400, type: 'part', number: sku, brand: this.cleanText(data.vendor) };

    const barcode = this.exactValueMatch(data.barcodes || []);
    if (barcode) return { rank: 390, type: 'barcode', number: barcode, brand: '' };

    const oe = this.groupMatch(this.referenceGroups(data.oeReferences, 'OE / OEM'));
    if (oe) return { rank: 300, type: 'oe', ...oe };

    const cross = this.groupMatch(this.referenceGroups(data.crossReferences, 'Cross reference'));
    if (cross) return { rank: 200, type: 'cross', ...cross };

    return null;
  }

  renderReason(card, match) {
    const root = card.querySelector('[data-svaico-match-reason]');
    if (!root) return;

    const labels = {
      part: 'Matched part number',
      barcode: 'Matched barcode',
      oe: 'Matched OE number',
      cross: 'Matched cross-reference'
    };

    const label = labels[match.type] || 'Matched reference';
    const brand = match.brand ? ` <span class="product-match-reason__brand">· ${this.escapeHtml(match.brand)}</span>` : '';
    root.innerHTML = `<strong>${label}:</strong> <span class="product-match-reason__number">${this.escapeHtml(match.number)}</span>${brand}`;
    root.hidden = false;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }
}

function initSvaicoSearchMatchEnhancers(root = document) {
  root.querySelectorAll('[data-svaico-search-grid]').forEach((grid) => {
    if (grid.dataset.svaicoMatchEnhanced === 'true') return;
    grid.dataset.svaicoMatchEnhanced = 'true';
    new SvaicoSearchMatchEnhancer(grid).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSvaicoSearchMatchEnhancers());
} else {
  initSvaicoSearchMatchEnhancers();
}

if (!customElements.get('svaico-predictive-search')) {
  customElements.define('svaico-predictive-search', SvaicoPredictiveSearch);
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target.matches('[data-auto-submit]')) target.form?.submit();
});


class SvaicoVehiclePicker {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-vehicle-form]');
    this.make = root.querySelector('[data-vehicle-make]');
    this.model = root.querySelector('[data-vehicle-model]');
    this.year = root.querySelector('[data-vehicle-year]');
    this.power = root.querySelector('[data-vehicle-power]');
    this.reset = root.querySelector('[data-vehicle-reset]');
    this.status = root.querySelector('[data-vehicle-status]');
    this.data = Array.isArray(window.SVAICO_VEHICLES) ? window.SVAICO_VEHICLES : [];
    if (!this.form || !this.data.length) {
      if (this.status) this.status.textContent = 'Vehicle data has not been loaded yet.';
      return;
    }
    this.populate(this.make, this.unique(this.data.map(v => v.make)), 'Select make');
    this.make.addEventListener('change', () => this.onMake());
    this.model.addEventListener('change', () => this.onModel());
    this.year.addEventListener('change', () => this.onYear());
    this.power.addEventListener('change', () => this.updateStatus());
    this.reset.addEventListener('click', () => this.resetAll());
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.restoreSavedVehicle();
  }

  restoreSavedVehicle() {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem('svaicoVehicle') || 'null');
    } catch (error) {}

    if (!saved || !saved.make) return;

    const makeExists = [...this.make.options].some(option => option.value === saved.make);
    if (!makeExists) return;

    this.make.value = saved.make;
    this.onMake();

    const modelExists = [...this.model.options].some(option => option.value === saved.model);
    if (!modelExists) return;

    this.model.value = saved.model;
    this.onModel();

    const yearExists = [...this.year.options].some(option => option.value === saved.year);
    if (!yearExists) return;

    this.year.value = saved.year;
    this.onYear();

    const powerExists = [...this.power.options].some(option => option.value === saved.power);
    if (powerExists) this.power.value = saved.power;

    this.updateStatus();
  }

  unique(items) {
    return [...new Set(items.filter(Boolean))].sort((a,b) => String(a).localeCompare(String(b), undefined, {numeric:true}));
  }

  populate(select, values, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>` +
      values.map(value => `<option value="${this.escape(value)}">${this.escape(value)}</option>`).join('');
    select.disabled = values.length === 0;
  }

  escape(value) {
    return String(value).replace(/[&<>"']/g, char => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'
    })[char]);
  }

  onMake() {
    const rows = this.data.filter(v => v.make === this.make.value);
    this.populate(this.model, this.unique(rows.map(v => v.model)), 'Select model');
    this.populate(this.year, [], 'Select year');
    this.populate(this.power, [], 'Select engine power');
    this.updateStatus();
  }

  onModel() {
    const rows = this.data.filter(v => v.make === this.make.value && v.model === this.model.value);
    this.populate(this.year, this.unique(rows.map(v => v.year)), 'Select year');
    this.populate(this.power, [], 'Select engine power');
    this.updateStatus();
  }

  onYear() {
    const rows = this.data.filter(v =>
      v.make === this.make.value &&
      v.model === this.model.value &&
      v.year === this.year.value
    );
    this.populate(this.power, this.unique(rows.map(v => v.power)), 'Select engine power');
    this.updateStatus();
  }

  updateStatus() {
    const values = [this.make.value, this.model.value, this.year.value, this.power.value].filter(Boolean);
    this.status.textContent = values.length ? values.join(' • ') : '';
  }

  resetAll() {
    try {
      localStorage.removeItem('svaicoVehicle');
    } catch (error) {}
    this.make.value = '';
    this.populate(this.model, [], 'Select model');
    this.populate(this.year, [], 'Select year');
    this.populate(this.power, [], 'Select engine power');
    this.updateStatus();
  }

  vehicleId() {
    return [
      this.make.value.trim(),
      this.model.value.trim(),
      this.year.value.trim(),
      this.power.value.trim()
    ].join('|');
  }

  submit(event) {
    event.preventDefault();
    const required = [this.make.value, this.model.value, this.year.value, this.power.value];
    if (required.some(value => !value)) {
      this.status.textContent = 'Select make, model, year and engine power.';
      return;
    }

    const params = new URLSearchParams();
    params.set('make', this.make.value);
    params.set('model', this.model.value);
    params.set('year', this.year.value);
    params.set('power', this.power.value);

    try {
      const selectedVehicle = {
        make: this.make.value,
        model: this.model.value,
        year: this.year.value,
        power: this.power.value,
        vehicleId: this.vehicleId()
      };

      localStorage.setItem('svaicoVehicle', JSON.stringify(selectedVehicle));

      let history = [];
      try {
        history = JSON.parse(localStorage.getItem('svaicoVehicleHistory') || '[]');
      } catch (error) {
        history = [];
      }

      history = history.filter(item => item.vehicleId !== selectedVehicle.vehicleId);
      history.unshift(selectedVehicle);
      history = history.slice(0, 5);
      localStorage.setItem('svaicoVehicleHistory', JSON.stringify(history));
    } catch (error) {}

    window.location.href = `/collections/all?view=vehicle-parts&${params.toString()}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-vehicle-picker]').forEach(root => new SvaicoVehiclePicker(root));
});


class SvaicoPriceSlider {
  constructor(root) {
    this.root = root;
    this.minRange = root.querySelector('[data-price-min-range]');
    this.maxRange = root.querySelector('[data-price-max-range]');
    this.minNumber = root.querySelector('[data-price-min-number]');
    this.maxNumber = root.querySelector('[data-price-max-number]');
    this.progress = root.querySelector('[data-price-progress]');
    this.rangeMax = Math.max(0, Number(root.dataset.rangeMax || 0));
    this.currency = root.dataset.currency || '';
    this.minimumGap = this.rangeMax >= 100 ? 1 : 0;

    if (!this.minRange || !this.maxRange || !this.minNumber || !this.maxNumber) return;

    this.minRange.addEventListener('input', () => this.fromMinRange());
    this.maxRange.addEventListener('input', () => this.fromMaxRange());
    this.minNumber.addEventListener('input', () => this.fromMinNumber());
    this.maxNumber.addEventListener('input', () => this.fromMaxNumber());
    this.minNumber.addEventListener('blur', () => this.commitNumbers());
    this.maxNumber.addEventListener('blur', () => this.commitNumbers());

    [this.minNumber, this.maxNumber].forEach(input => {
      input.addEventListener('wheel', event => {
        event.preventDefault();
        input.blur();
      }, { passive: false });
    });

    this.commitNumbers();
  }

  clamp(value, minimum, maximum) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return minimum;
    return Math.min(maximum, Math.max(minimum, parsed));
  }

  fromMinRange() {
    const maxValue = Number(this.maxRange.value);
    const minValue = this.clamp(this.minRange.value, 0, maxValue - this.minimumGap);
    this.minRange.value = minValue;
    this.minNumber.value = Math.round(minValue);
    this.render();
  }

  fromMaxRange() {
    const minValue = Number(this.minRange.value);
    const maxValue = this.clamp(this.maxRange.value, minValue + this.minimumGap, this.rangeMax);
    this.maxRange.value = maxValue;
    this.maxNumber.value = Math.round(maxValue);
    this.render();
  }

  fromMinNumber() {
    const maxValue = Number(this.maxNumber.value || this.rangeMax);
    const minValue = this.clamp(this.minNumber.value, 0, Math.max(0, maxValue - this.minimumGap));
    this.minNumber.value = Math.round(minValue);
    this.minRange.value = minValue;
    this.render();
  }

  fromMaxNumber() {
    const minValue = Number(this.minNumber.value || 0);
    const maxValue = this.clamp(this.maxNumber.value, minValue + this.minimumGap, this.rangeMax);
    this.maxNumber.value = Math.round(maxValue);
    this.maxRange.value = maxValue;
    this.render();
  }

  commitNumbers() {
    let minValue = this.clamp(this.minNumber.value, 0, this.rangeMax);
    let maxValue = this.clamp(this.maxNumber.value, 0, this.rangeMax);

    if (maxValue < minValue + this.minimumGap) {
      maxValue = Math.min(this.rangeMax, minValue + this.minimumGap);
      if (maxValue < minValue) minValue = maxValue;
    }

    this.minNumber.value = Math.round(minValue);
    this.maxNumber.value = Math.round(maxValue);
    this.minRange.value = minValue;
    this.maxRange.value = maxValue;
    this.render();
  }

  render() {
    const minValue = Number(this.minRange.value);
    const maxValue = Number(this.maxRange.value);
    const denominator = this.rangeMax || 1;
    const minPercent = (minValue / denominator) * 100;
    const maxPercent = (maxValue / denominator) * 100;

    if (this.progress) {
      this.progress.style.left = `${minPercent}%`;
      this.progress.style.right = `${100 - maxPercent}%`;
    }

    this.minNumber.max = Math.max(0, maxValue - this.minimumGap);
    this.maxNumber.min = Math.min(this.rangeMax, minValue + this.minimumGap);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-price-slider]').forEach(root => new SvaicoPriceSlider(root));
});


document.addEventListener('DOMContentLoaded', () => {
  let savedVehicle = null;
  try {
    savedVehicle = JSON.parse(localStorage.getItem('svaicoVehicle') || 'null');
  } catch (error) {}

  if (!savedVehicle || !savedVehicle.vehicleId) return;

  document.querySelectorAll('a[href^="/collections/"]').forEach(link => {
    if (link.hasAttribute('data-category-url') || link.hasAttribute('data-all-parts-link')) return;
    const url = new URL(link.href, window.location.origin);

    // Only retain fitment on product-list destinations, never overwrite an explicit fitment.
    if (!url.searchParams.has('filter.p.m.custom.vehicle_ids')) {
      url.searchParams.set('filter.p.m.custom.vehicle_ids', savedVehicle.vehicleId);
      link.href = url.pathname + '?' + url.searchParams.toString();
    }
  });
});


class SvaicoVehicleHeader {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-header-vehicle-trigger]');
    this.popover = root.querySelector('[data-vehicle-popover]');
    this.close = root.querySelector('[data-vehicle-popover-close]');
    this.indicator = root.querySelector('[data-header-vehicle-indicator]');
    this.current = root.querySelector('[data-popover-current-vehicle]');
    this.meta = root.querySelector('[data-popover-current-meta]');
    this.historyList = root.querySelector('[data-vehicle-history-list]');
    this.clearHistory = root.querySelector('[data-clear-vehicle-history]');

    if (!this.trigger || !this.popover) return;

    this.trigger.addEventListener('click', event => {
      event.stopPropagation();
      this.toggle();
    });

    this.close?.addEventListener('click', () => this.hide());
    this.clearHistory?.addEventListener('click', () => this.clear());

    document.addEventListener('click', event => {
      if (!this.root.contains(event.target)) this.hide();
    });
    document.addEventListener('svaico:vehicle-selected', () => this.render());

    this.render();
  }

  getCurrent() {
    try {
      return JSON.parse(localStorage.getItem('svaicoVehicle') || 'null');
    } catch (error) {
      return null;
    }
  }

  getHistory() {
    try {
      return JSON.parse(localStorage.getItem('svaicoVehicleHistory') || '[]');
    } catch (error) {
      return [];
    }
  }

  toggle() {
    if (this.popover.hidden) this.show();
    else this.hide();
  }

  show() {
    this.render();
    this.popover.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  hide() {
    this.popover.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  clear() {
    localStorage.removeItem('svaicoVehicleHistory');
    this.render();
  }

  select(vehicle) {
    localStorage.setItem('svaicoVehicle', JSON.stringify(vehicle));

    let history = this.getHistory().filter(item => item.vehicleId !== vehicle.vehicleId);
    history.unshift(vehicle);
    history = history.slice(0, 5);
    localStorage.setItem('svaicoVehicleHistory', JSON.stringify(history));

    this.render();
    this.hide();

    const hp = String(vehicle.hp || vehicle.power || '').replace(/\s*HP\s*$/i, '');
    if (vehicle.makeId && vehicle.modelId && vehicle.year && hp) {
      const url = new URL('/collections/all?view=vehicle-parts', window.location.origin);
      url.searchParams.set('make_id', vehicle.makeId);
      url.searchParams.set('model_id', vehicle.modelId);
      url.searchParams.set('year', vehicle.year);
      url.searchParams.set('hp', hp);
      window.location.href = `${url.pathname}?${url.searchParams.toString()}`;
      return;
    }

    window.location.href = '/#vehicle-picker';
  }

  render() {
    const current = this.getCurrent();
    const history = this.getHistory();

    if (current && current.vehicleId) {
      this.indicator.hidden = false;
      this.current.textContent = [current.make, current.model].filter(Boolean).join(' ');
      this.meta.textContent = [current.year, current.power].filter(Boolean).join(' • ');
    } else {
      this.indicator.hidden = true;
      this.current.textContent = 'No vehicle selected';
      this.meta.textContent = '';
    }

    if (!history.length) {
      this.historyList.innerHTML = '<div class="vehicle-history-empty">No recent vehicles yet.</div>';
      return;
    }

    this.historyList.innerHTML = '';
    history.forEach(vehicle => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vehicle-history-item';
      if (current && current.vehicleId === vehicle.vehicleId) button.classList.add('is-current');
      button.innerHTML = `
        <span>
          <strong>${[vehicle.make, vehicle.model].filter(Boolean).join(' ')}</strong>
          <small>${[vehicle.year, vehicle.power].filter(Boolean).join(' • ')}</small>
        </span>
        <span>↗</span>
      `;
      button.addEventListener('click', () => this.select(vehicle));
      this.historyList.appendChild(button);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-header-vehicle]').forEach(root => new SvaicoVehicleHeader(root));
});


/* Product fitment status beside Add to cart.
   V6: verifies compatibility against the same R2 vehicle product-set data used by
   the vehicle finder. Shopify product vehicle metafields are only a legacy fast path. */
class SvaicoProductFitmentStatus {
  constructor(root) {
    this.root = root;
    this.form = root.closest('.product-purchase-form');
    this.dataNode = this.form?.querySelector('[data-product-fitment-data]');
    this.title = root.querySelector('[data-product-fitment-status-title]');
    this.text = root.querySelector('[data-product-fitment-status-text]');
    this.icon = root.querySelector('[data-product-fitment-status-icon]');
    this.change = root.querySelector('[data-product-fitment-status-change]');
    this.data = this.readData();
    this.requestToken = 0;
    this.version = '';
    this.render();

    document.addEventListener('svaico:vehicle-selected', () => this.render());
    window.addEventListener('storage', event => {
      if (event.key === 'svaicoVehicle') this.render();
    });
  }

  readData() {
    try { return JSON.parse(this.dataNode?.textContent || '{}'); }
    catch (_) { return {}; }
  }

  selectedVehicle() {
    try { return JSON.parse(localStorage.getItem('svaicoVehicle') || 'null'); }
    catch (_) { return null; }
  }

  cleanUrl(value) {
    return String(value ?? '').trim().replace(/\/+$/, '');
  }

  normalize(value) {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/&AMP;/g, '&')
      .replace(/[^A-Z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  normalizePower(value) {
    const source = String(value ?? '').replace(',', '.');
    const number = source.match(/\d+(?:\.\d+)?/);
    return number ? String(Number(number[0])) : this.normalize(source);
  }

  normalizeHandle(value) {
    return String(value ?? '')
      .trim()
      .replace(/^https?:\/\/[^/]+\/products\//i, '')
      .replace(/^\/?products\//i, '')
      .split(/[?#]/)[0]
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();
  }

  normalizeVehicleId(value) {
    const parts = String(value ?? '').split('|');
    if (parts.length < 4) return this.normalize(value);
    return [
      this.normalize(parts[0]),
      this.normalize(parts[1]),
      this.normalize(parts[2]),
      this.normalizePower(parts[3])
    ].join('|');
  }

  selectedVehicleId(vehicle) {
    if (!vehicle) return '';
    return [
      this.normalize(vehicle.make),
      this.normalize(vehicle.model),
      this.normalize(vehicle.year),
      this.normalizePower(vehicle.power || vehicle.hp)
    ].join('|');
  }

  vehicleLabel(vehicle) {
    return [vehicle?.make, vehicle?.model, vehicle?.year, vehicle?.power || (vehicle?.hp ? `${vehicle.hp} HP` : '')]
      .filter(Boolean).join(' • ');
  }

  hasLegacyFitmentData() {
    return (Array.isArray(this.data?.vehicleIds) && this.data.vehicleIds.length > 0) ||
      (Array.isArray(this.data?.compatibleVehicles) && this.data.compatibleVehicles.length > 0);
  }

  matchesLegacy(vehicle) {
    const selected = this.selectedVehicleId(vehicle);
    if (!selected) return null;

    const ids = Array.isArray(this.data?.vehicleIds) ? this.data.vehicleIds : [];
    if (ids.some(id => this.normalizeVehicleId(id) === selected)) return true;

    const vehicles = Array.isArray(this.data?.compatibleVehicles) ? this.data.compatibleVehicles : [];
    if (vehicles.some(item => {
      if (item?.vehicle_id && this.normalizeVehicleId(item.vehicle_id) === selected) return true;
      return [
        this.normalize(item?.make),
        this.normalize(item?.model),
        this.normalize(item?.year ?? item?.years),
        this.normalizePower(item?.power ?? item?.hp)
      ].join('|') === selected;
    })) return true;

    return false;
  }

  setState(state, title, text, icon) {
    this.root.hidden = false;
    this.root.classList.remove(
      'product-fitment-status--compatible',
      'product-fitment-status--incompatible',
      'product-fitment-status--checking',
      'product-fitment-status--unknown'
    );
    if (state) this.root.classList.add(`product-fitment-status--${state}`);
    if (this.title) this.title.textContent = title || '';
    if (this.text) this.text.textContent = text || '';
    if (this.icon) this.icon.textContent = icon || '';
  }

  hide() {
    this.root.hidden = true;
    this.root.classList.remove(
      'product-fitment-status--compatible',
      'product-fitment-status--incompatible',
      'product-fitment-status--checking',
      'product-fitment-status--unknown'
    );
  }

  async fetchJson(relative, withVersion = true) {
    const baseUrl = this.cleanUrl(this.data?.fitmentBaseUrl);
    if (!baseUrl) throw new Error('Missing R2 fitment URL');
    const url = new URL(`${baseUrl}/${String(relative || '').replace(/^\/+/, '')}`);
    if (withVersion && this.version) url.searchParams.set('v', this.version);
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'force-cache'
    });
    if (!response.ok) throw new Error(`${response.status} ${relative}`);
    return response.json();
  }

  productValueHandle(value, products) {
    if (Number.isInteger(value) && Array.isArray(products)) {
      return this.normalizeHandle(products[value]);
    }
    if (typeof value === 'string') return this.normalizeHandle(value);
    if (value && typeof value === 'object') {
      return this.normalizeHandle(value.handle || value.product_handle || value.productHandle || '');
    }
    return '';
  }

  definitionContainsHandle(definition, products, wantedHandle) {
    const wanted = this.normalizeHandle(wantedHandle);
    if (!wanted || !definition) return false;

    const categories = Array.isArray(definition.categories) ? definition.categories : [];
    for (const category of categories) {
      const subgroups = Array.isArray(category?.subgroups) ? category.subgroups : [];
      for (const subgroup of subgroups) {
        if (!Array.isArray(subgroup?.products)) continue;
        for (const value of subgroup.products) {
          if (this.productValueHandle(value, products) === wanted) return true;
        }
      }
    }
    return false;
  }

  objectContainsHandle(value, wantedHandle) {
    const wanted = this.normalizeHandle(wantedHandle);
    if (!wanted || value == null) return false;

    if (typeof value === 'string') return this.normalizeHandle(value) === wanted;
    if (Array.isArray(value)) return value.some(item => this.objectContainsHandle(item, wanted));
    if (typeof value !== 'object') return false;

    const direct = this.normalizeHandle(value.handle || value.product_handle || value.productHandle || '');
    if (direct && direct === wanted) return true;

    return Object.values(value).some(item => this.objectContainsHandle(item, wanted));
  }

  shardFilesFromSet(payload) {
    const files = [];
    const seen = new Set();
    const categories = Array.isArray(payload?.categories) ? payload.categories : [];

    for (const category of categories) {
      const subgroups = Array.isArray(category?.subgroups) ? category.subgroups : [];
      for (const subgroup of subgroups) {
        const shards = Array.isArray(subgroup?.shards) ? subgroup.shards : [];
        for (const shard of shards) {
          const file = String(shard?.file || '').trim();
          if (file && !seen.has(file)) {
            seen.add(file);
            files.push(file);
          }
        }
      }
    }
    return files;
  }

  async externalSetContainsHandle(modelData, setId, storage, wantedHandle) {
    const makeId = String(this.currentVehicle.makeId);
    const modelId = String(this.currentVehicle.modelId);
    const storageFile = String(storage.file || '').replace(/^\/+/, '');
    const relative = `${makeId}/${modelId}/${storageFile}`.replace(/\/+/g, '/');
    const payload = await this.fetchJson(relative);

    /* An external set file belongs to one selected vehicle set, so any exact
       handle found in it is a valid compatible product. */
    if (this.objectContainsHandle(payload, wantedHandle)) return true;

    const shardFiles = this.shardFilesFromSet(payload);
    if (!shardFiles.length) return false;

    const slash = storageFile.lastIndexOf('/');
    const baseDir = slash >= 0 ? storageFile.slice(0, slash) : '';

    /* Keep network use controlled: inspect page shards four at a time and stop
       immediately once the product handle is found. */
    for (let index = 0; index < shardFiles.length; index += 4) {
      const batch = shardFiles.slice(index, index + 4);
      const results = await Promise.all(batch.map(async file => {
        const shardRelative = `${makeId}/${modelId}/${baseDir ? `${baseDir}/` : ''}${file}`.replace(/\/+/g, '/');
        try {
          const shardPayload = await this.fetchJson(shardRelative);
          return this.objectContainsHandle(shardPayload, wantedHandle);
        } catch (error) {
          console.warn('SVAICO fitment shard check failed', error);
          return false;
        }
      }));
      if (results.some(Boolean)) return true;
    }
    return false;
  }

  async r2Matches(vehicle) {
    const baseUrl = this.cleanUrl(this.data?.fitmentBaseUrl);
    const wantedHandle = this.normalizeHandle(this.data?.productHandle);
    const makeId = String(vehicle?.makeId || '').trim();
    const modelId = String(vehicle?.modelId || '').trim();
    const year = String(vehicle?.year || '').trim();
    const hp = String(vehicle?.hp || String(vehicle?.power || '').replace(/\s*HP\s*$/i, '')).trim();

    if (!baseUrl || !wantedHandle || !makeId || !modelId || !year || !hp) return null;

    this.currentVehicle = { ...vehicle, makeId, modelId, year, hp };

    try {
      const manifest = await this.fetchJson('manifest.json', false);
      this.version = String(manifest?.built_at || manifest?.registry_uuid || manifest?.schema_version || '');
    } catch (_) {
      this.version = '';
    }

    const modelPath = `${makeId}/${modelId}/index.json`.replace(/\/+/g, '/');
    const modelData = await this.fetchJson(modelPath);
    const setId = modelData?.routes?.[year]?.[hp];
    if (!setId) return false;

    if (modelData.storage_mode === 'model-inline-deduplicated') {
      const definition = modelData?.sets?.[setId];
      if (!definition) return null;
      return this.definitionContainsHandle(definition, modelData?.products || [], wantedHandle);
    }

    const storage = modelData?.set_storage?.[setId];
    if (!storage?.file) return null;

    const makeModelPrefix = `${makeId}/${modelId}/`;
    const relative = `${makeModelPrefix}${String(storage.file).replace(/^\/+/, '')}`.replace(/\/+/g, '/');
    const payload = await this.fetchJson(relative);

    if (storage.mode === 'shard') {
      const definition = payload?.sets?.[setId];
      if (!definition) return null;
      return this.definitionContainsHandle(definition, payload?.products || [], wantedHandle);
    }

    if (storage.mode === 'external') {
      /* Reuse the already fetched payload first to avoid a duplicate request. */
      if (this.objectContainsHandle(payload, wantedHandle)) return true;

      const shardFiles = this.shardFilesFromSet(payload);
      if (!shardFiles.length) return false;

      const storageFile = String(storage.file || '').replace(/^\/+/, '');
      const slash = storageFile.lastIndexOf('/');
      const baseDir = slash >= 0 ? storageFile.slice(0, slash) : '';

      for (let index = 0; index < shardFiles.length; index += 4) {
        const batch = shardFiles.slice(index, index + 4);
        const results = await Promise.all(batch.map(async file => {
          const shardRelative = `${makeId}/${modelId}/${baseDir ? `${baseDir}/` : ''}${file}`.replace(/\/+/g, '/');
          try {
            const shardPayload = await this.fetchJson(shardRelative);
            return this.objectContainsHandle(shardPayload, wantedHandle);
          } catch (error) {
            console.warn('SVAICO fitment shard check failed', error);
            return false;
          }
        }));
        if (results.some(Boolean)) return true;
      }
      return false;
    }

    return null;
  }

  cacheKey(vehicle) {
    const vehicleId = vehicle?.vehicleId || [
      vehicle?.makeId,
      vehicle?.modelId,
      vehicle?.year,
      vehicle?.hp || String(vehicle?.power || '').replace(/\s*HP\s*$/i, '')
    ].filter(Boolean).join('-');
    return `svaicoFitmentCheck:v6:${vehicleId}:${this.normalizeHandle(this.data?.productHandle)}`;
  }

  readCache(vehicle) {
    try {
      const raw = sessionStorage.getItem(this.cacheKey(vehicle));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.compatible !== 'boolean') return null;
      return parsed.compatible;
    } catch (_) {
      return null;
    }
  }

  writeCache(vehicle, compatible) {
    try {
      sessionStorage.setItem(this.cacheKey(vehicle), JSON.stringify({
        compatible: Boolean(compatible),
        checkedAt: Date.now()
      }));
    } catch (_) {}
  }

  async render() {
    const token = ++this.requestToken;
    const vehicle = this.selectedVehicle();

    if (!vehicle || !vehicle.make || !vehicle.model || !vehicle.year || !(vehicle.power || vehicle.hp)) {
      this.hide();
      return;
    }

    const label = this.vehicleLabel(vehicle);

    /* Legacy Shopify data is accepted when present, but current SVAICO fitment
       normally lives in R2 because the large Shopify vehicle metafields were removed. */
    if (this.hasLegacyFitmentData()) {
      const compatible = this.matchesLegacy(vehicle);
      if (compatible === true) {
        this.setState('compatible', 'Fits your selected vehicle', label, '✓');
        return;
      }
      /* A legacy miss is not treated as final because old metafields can be
         incomplete. Verify the selected R2 product set before showing a warning. */
    }

    const cached = this.readCache(vehicle);
    if (typeof cached === 'boolean') {
      if (cached) this.setState('compatible', 'Fits your selected vehicle', label, '✓');
      else this.setState('incompatible', 'This part does not fit your selected vehicle', label, '!');
      return;
    }

    this.setState('checking', 'Checking fitment…', label, '…');

    try {
      const compatible = await this.r2Matches(vehicle);
      if (token !== this.requestToken) return;

      if (compatible === true) {
        this.writeCache(vehicle, true);
        this.setState('compatible', 'Fits your selected vehicle', label, '✓');
      } else if (compatible === false) {
        this.writeCache(vehicle, false);
        this.setState('incompatible', 'This part does not fit your selected vehicle', label, '!');
      } else {
        this.setState('unknown', 'Fitment could not be verified', `${label} • Please confirm before ordering.`, '?');
      }
    } catch (error) {
      if (token !== this.requestToken) return;
      console.warn('SVAICO R2 product fitment check failed', error);
      this.setState('unknown', 'Fitment could not be verified', `${label} • Please confirm before ordering.`, '?');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-fitment-status]').forEach(root => new SvaicoProductFitmentStatus(root));
});
